import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { UiSelectableChipsComponent } from '@/ui/selectable-chips/ui-selectable-chips';
import { UiToggleComponent } from '@/ui/toggle/ui-toggle';
import { arraysContainSameElements } from '@/utils/toolkit/array/arrays-contain-same-elements.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, type OnChanges, type OnInit, Output, type SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, type UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, type Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators';
import { SortOrder } from '../../../../data-viz/data-source.interface';
import { AdvizeoInputNumberDirective } from '../../../../directives/advizeo-input/advizeo-input-number.directive';
import { AdvizeoInputDirective } from '../../../../directives/advizeo-input/advizeo-input.directive';
import {
    type BusinessIntelligenceDimension,
    type BusinessIntelligenceMeasure,
    type BusinessIntelligenceMember,
    BusinessIntelligenceMemberType,
    type BusinessIntelligenceSortCriterion
} from '../../../../interfaces/BusinessIntelligence.interface';
import {
    type BusinessIntelligenceTableRepresentationConfig,
    isBusinessIntelligenceDimension
} from '../../../../interfaces/BusinessIntelligenceRepresentation.interface';
import { BusinessIntelligenceRepresentationConfigEditorComponent } from '../business-intelligence-representation-config-editor.class';

const maxColumns = 10;
@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-table-representation-config-editor.scss'],
    selector: 'business-intelligence-table-representation-config-editor',
    templateUrl: 'business-intelligence-table-representation-config-editor.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        UiFormFieldComponent,
        UiSelectableChipsComponent,
        UiSelectComponent,
        UiToggleComponent,
        AdvizeoInputDirective,
        AdvizeoInputNumberDirective,
        AsyncPipe,
        TranslatePipe
    ]
})
export class BusinessIntelligenceTableRepresentationConfigEditorComponent
    extends BusinessIntelligenceRepresentationConfigEditorComponent<BusinessIntelligenceTableRepresentationConfig>
    implements OnInit, OnChanges
{
    private _fb = inject(UntypedFormBuilder);

    private _observableInputsManager = new ObservableInputsManager<BusinessIntelligenceTableRepresentationConfigEditorComponent>(this);

    @Input() config!: BusinessIntelligenceTableRepresentationConfig;
    config$ = this._observableInputsManager.observe('config');

    @Input() availableMeasures: BusinessIntelligenceMeasure[] = [];
    availableMeasures$ = this._observableInputsManager.observe('availableMeasures');

    @Input() availableDimensions: BusinessIntelligenceDimension[] = [];
    availableDimensions$ = this._observableInputsManager.observe('availableDimensions');

    @Input() disabled!: boolean;
    @Output() configChanged: EventEmitter<BusinessIntelligenceTableRepresentationConfig> = new EventEmitter();

    formGroup!: UntypedFormGroup;
    columnMembers$!: Observable<BusinessIntelligenceMember[]>;

    DEFAULT_LIMIT: number = 50;

    ngOnInit() {
        const dimensions$ = this.availableDimensions$.pipe(filter(isNotNullish), distinctUntilChanged(arraysContainSameElements));

        this.columnMembers$ = combineLatest([
            dimensions$.pipe(distinctUntilChanged(arraysContainSameElements)),
            this.availableMeasures$.pipe(distinctUntilChanged(arraysContainSameElements))
        ]).pipe(
            map(([dimensions, measures]) => {
                return [
                    ...dimensions.map(dimension => ({
                        ...dimension,
                        memberCode: dimension.dimensionCode
                    })),
                    ...measures.map(measure => ({
                        ...measure,
                        memberCode: measure.measureCode
                    }))
                ];
            })
        );

        this.formGroup = this._fb.group({
            columns: [null, [Validators.required, Validators.maxLength(maxColumns)]],
            sortMemberCode: null,
            isSortReversed: false,
            displayLinesWithNullValues: false,
            limit: [null]
        });

        this.config$
            .pipe(
                withLatestFrom(
                    dimensions$.pipe(distinctUntilChanged(arraysContainSameElements)),
                    this.availableMeasures$.pipe(distinctUntilChanged(arraysContainSameElements))
                ),
                untilDestroyed(this)
            )
            .subscribe(([config, dimensions, measures]) => {
                if (!config) {
                    this.formGroup.setValue(
                        {
                            columns: null,
                            sortMemberCode: null,
                            isSortReversed: false,
                            displayLinesWithNullValues: false,
                            limit: null
                        },
                        { emitEvent: false }
                    );
                } else {
                    const columns = config.members
                        ? config.members
                              .map(member => {
                                  if (isBusinessIntelligenceDimension(member)) {
                                      const dimension = dimensions.find(
                                          dimension => dimension.dimensionCode === member.memberConfiguration.dimensionCode
                                      );
                                      return dimension ? { ...dimension, memberCode: dimension.dimensionCode } : null;
                                  } else {
                                      const measure = measures.find(
                                          measure => measure.measureCode === member.memberConfiguration.measureCode
                                      );
                                      return measure ? { ...measure, memberCode: measure.measureCode } : null;
                                  }
                              })
                              .filter(isNotNullish)
                        : null;
                    const sortCriterion = this.getSortMemberCode(columns!, config);
                    this.formGroup.setValue(
                        {
                            columns,
                            sortMemberCode: sortCriterion ? sortCriterion.memberCode : null,
                            isSortReversed: sortCriterion ? sortCriterion.sortDirection === SortOrder.DESC : false,
                            displayLinesWithNullValues: config.displayLinesWithNullValues ?? false, // "?? false" is for retro compatibility
                            limit: config.limit ? config.limit : null
                        },
                        { emitEvent: false }
                    );
                }
            });

        this.formGroup.valueChanges
            .pipe(
                withLatestFrom(
                    dimensions$.pipe(distinctUntilChanged(arraysContainSameElements)),
                    this.availableMeasures$.pipe(distinctUntilChanged(arraysContainSameElements))
                ),
                untilDestroyed(this)
            )
            .subscribe(([formValue, dimensions, measures]) => {
                const sortCriteria: BusinessIntelligenceSortCriterion[] = this.getSortCriteria(formValue);
                this.configChanged.emit({
                    members: formValue.columns
                        .map((member: BusinessIntelligenceMember) => {
                            const dimension = dimensions.find(dimension => dimension.dimensionCode === member.memberCode);
                            const measure = measures.find(measure => measure.measureCode === member.memberCode);
                            if (dimension) {
                                return {
                                    type: BusinessIntelligenceMemberType.DIMENSION,
                                    memberConfiguration: { dimensionCode: dimension.dimensionCode }
                                };
                            } else if (measure) {
                                return {
                                    type: BusinessIntelligenceMemberType.MEASURE,
                                    memberConfiguration: { measureCode: measure.measureCode }
                                };
                            } else {
                                return;
                            }
                        })
                        .filter(isNotNullish),
                    sortCriteria,
                    limit: formValue.limit,
                    displayLinesWithNullValues: formValue.displayLinesWithNullValues
                });
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        this._observableInputsManager.triggerChanges(changes);
    }

    private getSortCriteria(formValue: any): BusinessIntelligenceSortCriterion[] {
        if (formValue.sortMemberCode) {
            return [
                {
                    memberCode: formValue.sortMemberCode,
                    sortDirection: formValue.isSortReversed ? SortOrder.DESC : SortOrder.ASC
                } as BusinessIntelligenceSortCriterion
            ];
        }
        return [];
    }

    private getSortMemberCode(
        columns: BusinessIntelligenceMember[],
        config: BusinessIntelligenceTableRepresentationConfig
    ): BusinessIntelligenceSortCriterion | null {
        if (!config.sortCriteria || !config.sortCriteria.length) {
            return null;
        }
        const memberCodes = columns.map(member => member.memberCode);
        const validSortCriteriaCandidates = config.sortCriteria.filter(sortCriterion => memberCodes.includes(sortCriterion.memberCode));
        if (!validSortCriteriaCandidates.length) {
            return null;
        }
        return validSortCriteriaCandidates[0];
    }
}
